import { SyntheticEvent, memo, useCallback, useMemo } from 'react';
import { translateInComponent } from 'infras/i18n';
import translations from '../i18n.json';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  mediaMin,
  spacing,
  zIndex,
} from 'presentations/views/styles';
import Avatar, { Group } from 'presentations/views/components/atoms/Avatar';
import {
  black,
  gray02,
  gray04,
  gray06,
  primary,
  white,
} from 'presentations/views/styles/color';
import { DateTime, formatToTimeShort } from 'infras/datetime';
import styled from '@emotion/styled';
import EventCell from '../EventCell';
import Icon from 'presentations/views/components/atoms/Icon';
import { useDayTimeGrid } from './hooks';
import { css } from '@emotion/react';
import {
  Behavior,
  EventListItem,
  MergeableEventListItem,
  ScheduleType,
} from '../reducer';
import { getGroupsFromSlots } from '../reducer/useCalendarProvider';

interface Props {
  className?: string;
  startedAt: DateTime;
  onClickBookingSlot?: (event: EventListItem) => void;
  onClickEvent?: (event: MergeableEventListItem, el: HTMLElement) => void;
  isLoading?: boolean;
}

const Component = memo((props: Props) => {
  const { state, actions } = useDayTimeGrid({
    startedAt: props.startedAt,
    onClickEvent: props.onClickEvent,
  });

  const unixtime = useMemo(() => state.now.toSeconds(), [state.now]);

  const selectedGroups = useMemo(() => {
    const slots = state.events
      .filter((event) => event.scheduleType === ScheduleType.Availability)
      .map((event) => ({
        x: event.x,
        y: event.y,
        ySpan: event.ySpan,
        date: event.startedAt,
        isAllDay: event.isAllDay,
      }));
    return getGroupsFromSlots(slots, state.rulerStep);
  }, [state.events, state.rulerStep]);

  const renderNowBorder = useCallback(
    (date: DateTime) => {
      const diff = unixtime - date.toSeconds();
      const diffRatio = diff / 60 / state.rulerStep;
      if (0 <= diffRatio && diffRatio < 1) {
        return (
          <NowBorder color={state.primaryColor} ratio={diffRatio}>
            <NowBorderCircle color={state.primaryColor} />
          </NowBorder>
        );
      }
      return null;
    },
    [state.rulerStep, unixtime],
  );

  const t = translateInComponent(translations);

  const onClick = (e: SyntheticEvent<HTMLDivElement>) => {
    const index = e.currentTarget.dataset.index;
    if (index === undefined) return;
    const event = state.events[Number(index)];
    if (!event) return;
    if (!props.onClickBookingSlot) return;
    props.onClickBookingSlot(event);
  };

  return (
    <Calendar
      className={props.className}
      maxRow={state.maxRow}
      maxCol={state.dates.length}
    >
      {state.frames.map((frame: { date: DateTime }, i: number) => {
        const diff = unixtime - frame.date.toSeconds();
        return (
          <SlotTimeCell
            key={i}
            isDivider={frame.date.minute === 0}
            isTop={frame.date.hour === 0}
            isDisabled={diff > 0}
            isShowBorder={frame.date.minute % state.rulerBorderStep === 0}
          >
            {renderNowBorder(frame.date)}
          </SlotTimeCell>
        );
      })}

      {!props.isLoading &&
        !state.isLoading &&
        state.events.map((event, index) => {
          if (event.scheduleType === ScheduleType.Availability) {
            return (
              <BookingCell
                key={event.layoutId}
                data-index={index}
                x={event.x}
                y={event.y}
                ySpan={event.ySpan}
                onClick={onClick}
              >
                <HoverContent className="hover" color={state.primaryColor}>
                  <TimeContainer>
                    <Time>
                      {formatToTimeShort(event.startedAt)}
                      <Haifun>-</Haifun>
                      {formatToTimeShort(event.endedAt)}
                    </Time>
                    {event.customData?.capacity && (
                      <Capacity>{event.customData?.capacity.label}</Capacity>
                    )}
                    {event.customData?.guestUsers &&
                      event.customData?.guestUsers.length > 0 && (
                        <UserList>
                          <StyledAvatarGroup max={3} spacing={4}>
                            {event.customData?.guestUsers.map((user: any) => (
                              <StyledAvatar
                                src={user.profileImage}
                                color={user.color}
                              >
                                {user.name.charAt(0)}
                              </StyledAvatar>
                            ))}
                          </StyledAvatarGroup>
                        </UserList>
                      )}
                    {/* <IconList>{accessory}</IconList> */}
                  </TimeContainer>
                </HoverContent>
              </BookingCell>
            );
          } else {
            const timeText = formatToTimeShort(event.startedAt);
            return (
              <EventCell
                key={event.layoutId}
                eventId={event.uid}
                x={event.x}
                y={event.y}
                ySpan={event.ySpan}
                xRatio={event.xRatio}
                xSpanRatio={event.xSpanRatio}
                xMargin={event.xMargin}
                color={event.color}
                colors={(event.mergedEvents || []).map((event) => event.color)}
                name={event.name}
                timeText={timeText}
                onClick={actions.onClickEvent}
                icon={
                  event.behavior === Behavior.Work && (
                    <ServiceIcon name="check-circle" />
                  )
                }
              />
            );
          }
        })}
      {!props.isLoading &&
        state.isShowGroup &&
        selectedGroups.map((selectedGroup, i) => {
          return (
            <SelectedGroupCell
              key={i}
              draggable
              x={selectedGroup.x}
              xSpan={selectedGroup.xSpan}
              y={selectedGroup.y}
              ySpan={selectedGroup.ySpan}
              isShowSlot={state.isShowSlot}
              color={state.primaryColor}
            >
              <GroupPrefix>{t('Booking slot default prefix')}</GroupPrefix>
              <GroupTime>
                <GroupTimeText>
                  {formatToTimeShort(selectedGroup.startedAt)}
                  <Haifun>-</Haifun>
                  {formatToTimeShort(selectedGroup.endedAt)}
                </GroupTimeText>
              </GroupTime>
            </SelectedGroupCell>
          );
        })}
    </Calendar>
  );
});

const Calendar = styled.div<{ maxRow: number; maxCol: number }>`
  width: inherit;
  flex: 1 1 0%;
  display: grid;
  grid-template-rows: ${(props) => `repeat(${props.maxRow}, minmax(8px, 1fr))`};
  grid-template-columns: ${(props) => `repeat(${props.maxCol}, 1fr)`};
  grid-auto-flow: column;
  position: relative;
  border-bottom: 1px solid ${gray04};
  background-color: ${gray06};
`;

export const SlotTimeCell = styled.div<{
  isDivider: boolean;
  isShowBorder: boolean;
  isTop: boolean;
  iconUrl?: string | null;
  isDisabled?: boolean;
  isHoliday?: boolean;
  isNotAvailable?: boolean;
}>`
  position: relative;
  border-color: ${gray04};
  border-left-width: 1px;
  border-left-style: solid;
  border-top-width: ${(props) =>
    !props.isShowBorder || (props.isTop && props.isDivider) ? 0 : '1px'};
  border-top-style: ${(props) => (props.isDivider ? 'solid' : 'dotted')};
  /* background-color: ${(props) => props.isHoliday && gray06}; */
  color: ${(props) => props.isHoliday && gray02};
  z-index: 4;
  /* background: ${(props) => props.isNotAvailable && gray06}; */
  /* background: ${gray06}; */
  user-select: none;
  pointer-events: none;

  ${mediaMin.smartphone} {
    ${(props) =>
      props.isDisabled &&
      css`
        user-select: none;
      `}
  }
`;

export const SlotCell = styled.div<{
  x: number;
  xSpan: number;
  y: number;
  ySpan: number;
  iconUrl?: string;
  isShowSlot: boolean;
  color?: string;
}>`
  grid-area: ${(props) =>
    `${props.y + 1} / ${props.x + 1} / span ${props.ySpan} / span ${
      props.xSpan
    }`}; // 縦位置 / 横位置 / 縦枠幅 / 横枠幅
  width: calc(100% - 8px);
  height: calc(100% - 8px);
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 3;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  border-radius: ${borderRadius(3)};
  padding: 0 ${spacing(4)};
  background-color: ${(props) => props.color || primary};
  color: ${white};
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  ${(props) =>
    !props.isShowSlot &&
    css`
      opacity: 0;
      ${mediaMin.tablet} {
        :hover {
          opacity: 1;
        }
      }
    `}
`;

export const GroupPrefix = styled.div`
  font-size: ${fontSize(10)};
  padding: 0 ${spacing(2)};
  margin-top: ${spacing(2)};
  margin-bottom: ${spacing(4)};
  line-height: 1;
`;
export const GroupTime = styled.div`
  font-weight: ${fontWeight('bold')};
  /* position: absolute;
  top: -6px;
  left: 0; */
  /* z-index: ${zIndex(100)}; */
  width: 100%;
  display: flex;
  /* align-items: center; */
  /* justify-content: space-between; */
  ${mediaMax.desktop} {
    font-size: ${fontSize(12)};
  }
`;
export const GroupTimeText = styled.div`
  font-size: ${fontSize(11)};
  display: flex;
  align-items: center;
  background-color: ${white};
  padding: 0 ${spacing(2)};
  /* margin-left: ${spacing(4)}; */
  line-height: 1;
`;
export const GroupTitleIcon = styled(Icon)`
  margin-right: ${spacing(2)};
  width: 14px !important;
  height: 14px !important;
`;
export const GroupTitle = styled.div`
  margin-right: ${spacing(2)};
`;
export const DraggableCell = styled.div`
  width: 100%;
  height: 100%;
  opacity: 0;
`;
export const Haifun = styled.span`
  margin: 0 1px;
  ${mediaMax.desktop} {
    margin: 0 1px;
  }
`;

export const NowBorderCircle = styled.div<{ color?: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => props.color || primary};
  position: absolute;
  top: -4px;
  left: -5px;
  z-index: ${zIndex(100)};
`;
export const NowBorder = styled.div<{ ratio: number; color?: string }>`
  width: 100%;
  height: 2px;
  background-color: ${(props) => props.color || primary};
  position: absolute;
  top: ${(props) => `calc(100% * ${props.ratio})`};
  left: 0;
  z-index: ${zIndex(100)};
`;
const ServiceIcon = styled(Icon)`
  width: 12px !important;
  height: 12px !important;
  color: inherit;
  margin-right: ${spacing(2)};
  :last-of-type {
    margin-right: 0;
  }
`;
const BookingCell = styled.div<{
  x: number;
  xRatio?: number;
  y: number;
  ySpan: number;
}>`
  z-index: 11;
  grid-area: ${(props) =>
    `${props.y + 1} / ${props.x + 1} / span ${
      props.ySpan
    } / span 1`}; // 縦位置 / 横位置 / 縦枠幅 / 横枠幅
  /* cursor: pointer; */
  overflow: hidden;
  top: 1px;
  left: 1px;
  width: calc(100% - 1px);
  height: calc(100% - 1px);
  position: absolute;
  padding: 0;
  /* background-color: ${white}; */
  display: flex;
  padding: ${spacing(2)};
  :hover {
    .hover {
      display: block;
    }
  }
`;

const HoverContent = styled.div`
  background-color: transparent;
  border-radius: ${borderRadius(3)};
  color: transparent;
  width: 100%;
  height: 100%;
  display: none;
  background-color: ${(props) => props.color || primary};
  color: ${white};
`;

const TimeContainer = styled.div`
  margin: ${spacing(4)} 5px 0 5px;
`;
const Capacity = styled.div`
  font-size: ${fontSize(11)};
  line-height: 1;
  margin-top: ${spacing(4)};
`;
const Time = styled.div`
  line-height: 1;
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(11)};
  /* letter-spacing: -0.01em; */
  margin-top: ${spacing(2)};
`;
export const SelectedGroupCell = styled.div<{
  x: number;
  xSpan: number;
  y: number;
  ySpan: number;
  isShowSlot: boolean;
  iconUrl?: string;
  color?: string;
}>`
  grid-area: ${(props) =>
    `${props.y + 1} / ${props.x + 1} / span ${props.ySpan} / span ${
      props.xSpan
    }`}; // 縦位置 / 横位置 / 縦枠幅 / 横枠幅
  width: calc(100% - 1px);
  height: calc(100% - 0px);
  position: absolute;
  z-index: 10;
  top: 0px;
  left: 0px;
  color: ${(props) => props.color || primary};
  padding: ${spacing(2)} ${spacing(4)};
  border-radius: ${borderRadius(6)};
  background-color: ${(props) =>
    props.isShowSlot ? `${white(props)}cc` : `${white(props)}cc`};
  border: 2px dotted ${(props) => props.color || primary};
  cursor: ${(props) =>
    props.iconUrl ? `url(${props.iconUrl}) 10 10, pointer` : 'pointer'};
`;
export const UserList = styled.div`
  display: flex;
  margin-top: ${spacing(2)};
`;
const StyledAvatarGroup = styled(Group)`
  .MuiAvatar-colorDefault {
    width: 18px;
    height: 18px;
    font-size: ${fontSize(10)};
  }
  .MuiAvatarGroup-avatar {
    border: 0;
  }
`;
const StyledAvatar = styled(Avatar)<{ color: string }>`
  width: 18px;
  height: 18px;
  font-size: ${fontSize(12)};
  font-weight: initial;

  ${({ color }) => css`
    background-color: ${color};
  `}
`;

export default Component;
